/**
 * AavaaLogo — the official brand mark.
 *
 * Concept:
 *   Two overlapping lens-like circles that form a soft "aa" (the doubled-a in
 *   "aavaa") — one sage, one warm terracotta. It reads as
 *     (a) two watchful, loving eyes,
 *     (b) a parent and child side-by-side,
 *     (c) the signature repeated syllable of the brand name.
 *
 * Variants:
 *   <AavaaLogo variant="mark" />      → square monogram, works as avatar / IG DP / favicon
 *   <AavaaLogo variant="wordmark" />  → mark + "aavaa" word, for nav / footer
 */
export default function AavaaLogo({
  variant = "mark",
  size = 44,
  className = "",
  accent = "wordmark", // optional color styling for wordmark
}) {
  if (variant === "wordmark") {
    return (
      <div className={`inline-flex items-center gap-2.5 ${className}`} data-testid="aavaa-logo-wordmark">
        <Mark size={size} />
        <span
          className="font-outfit font-semibold tracking-tight select-none"
          style={{ fontSize: size * 0.58, color: "#3D4A3F", lineHeight: 1 }}
        >
          aavaa
        </span>
      </div>
    );
  }
  return <Mark size={size} className={className} />;
}

function Mark({ size = 44, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Aavaa"
    >
      <defs>
        <linearGradient id="aavaa-body" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FDFBF6" />
          <stop offset="100%" stopColor="#F3EFE6" />
        </linearGradient>
        <linearGradient id="aavaa-sage" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#A7B399" />
          <stop offset="100%" stopColor="#7A8A6E" />
        </linearGradient>
        <linearGradient id="aavaa-warm" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E7C19A" />
          <stop offset="100%" stopColor="#D4A373" />
        </linearGradient>
      </defs>

      {/* Soft rounded body (warm cream) */}
      <rect x="0" y="0" width="64" height="64" rx="18" fill="url(#aavaa-body)" stroke="#E2DFD8" strokeWidth="1" />

      {/* Two overlapping lens circles — the "aa" / two watchful eyes */}
      {/* Sage (left) */}
      <circle cx="24" cy="32" r="13" fill="url(#aavaa-sage)" />
      {/* Warm terracotta (right) — slightly behind the sage via z-order,
          but visually it sits "outside" the overlap area */}
      <circle cx="40" cy="32" r="13" fill="url(#aavaa-warm)" />
      {/* Inner overlap highlight — subtle white sliver */}
      <path
        d="M 32 21 a 13 13 0 0 1 0 22 a 13 13 0 0 1 0 -22 Z"
        fill="#FDFBF6"
        opacity="0.28"
      />
      {/* Tiny pupils (centered dots) — lends the "eye" cue */}
      <circle cx="24" cy="32" r="2.2" fill="#3D4A3F" />
      <circle cx="40" cy="32" r="2.2" fill="#3D4A3F" />
    </svg>
  );
}
